import { type FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  LabelList,
} from "recharts";
import { Spin } from "antd";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import NoDataDisplay from "src/components/reusable/NoDataDisplay";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import { DefinitionTooltip } from "src/components/common";
import { formatNumber, isArabicLanguage, wrapBidiIsolate } from "src/utils/numberFormat";
import { CHART_GRID_STROKE, CHART_TOOLTIP_CURSOR } from "../../../../components/charts/ChartPalette";
import "./AudienceChart.scss";

export interface PredictedGroupTrend {
  changePercent: number;
  needsAttention: boolean;
}

export interface PredictedGroupBar {
  name: string;
  members: number;
  membersLabel: string;
  trend?: PredictedGroupTrend;
}

interface AudienceBarChartProps {
  data: PredictedGroupBar[];
  loading?: boolean;
}

interface GroupAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string; index: number };
  groups: PredictedGroupBar[];
  formatTrend: (_value: number) => string;
}

const ITEMS_PER_PAGE = 10;
const CHART_HEIGHT = 340;
const BAR_COLOR = "#a855f7";
const BAR_SIZE = 36;
const X_AXIS_HEIGHT = 56;
const PILL_WIDTH = 80;
const PILL_HEIGHT = 20;
const PILL_OFFSET_Y = 20;

const GroupAxisTick: FC<GroupAxisTickProps> = ({ x = 0, y = 0, payload, groups, formatTrend }) => {
  const trend = payload ? groups[payload.index]?.trend : undefined;

  return (
    <g transform={`translate(${x},${y})`}>
      <text className="audience-chart__tick-label" textAnchor="middle" dy={10}>
        {payload?.value}
      </text>
      {trend && (
        <foreignObject
          x={-PILL_WIDTH / 2}
          y={PILL_OFFSET_Y}
          width={PILL_WIDTH}
          height={PILL_HEIGHT}
        >
          <div className="audience-chart__pill-slot">
            <span
              className={`audience-chart__pill audience-chart__pill--${
                trend.needsAttention ? "negative" : "positive"
              }`}
            >
              {trend.changePercent >= 0 ? (
                <ArrowUp size={10} aria-hidden />
              ) : (
                <ArrowDown size={10} aria-hidden />
              )}
              {formatTrend(trend.changePercent)}
            </span>
          </div>
        </foreignObject>
      )}
    </g>
  );
};

const AudienceBarChart: FC<AudienceBarChartProps> = ({ data, loading = false }) => {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const isArabic = isArabicLanguage(i18n.language);
  const totalPages = Math.max(1, Math.ceil(data.length / ITEMS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(page => Math.min(page, totalPages));
  }, [totalPages]);

  const pageData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return data.slice(start, start + ITEMS_PER_PAGE);
  }, [data, currentPage]);

  const formatValue = useCallback(
    (value: number) => formatNumber(value ?? 0, {}, i18n.language),
    [i18n.language],
  );

  const formatTrend = useCallback(
    (value: number) => {
      const digits = formatNumber(
        Math.abs(value),
        { minimumFractionDigits: 1, maximumFractionDigits: 2 },
        i18n.language,
      );
      const token = `${value >= 0 ? "+" : "-"}${digits}%`;
      return isArabic ? wrapBidiIsolate(token) : token;
    },
    [i18n.language, isArabic],
  );

  const title = t("audienceOverview.predictedGroupsTitle");
  const PreviousIcon = isArabic ? ChevronRight : ChevronLeft;
  const NextIcon = isArabic ? ChevronLeft : ChevronRight;

  return (
    <section className="audience-chart">
      <div className="audience-chart__header">
        <div className="audience-chart__heading">
          <div className="audience-chart__title-row">
            <h3 className="audience-chart__title">{title}</h3>
            <DefinitionTooltip
              label={title}
              info={{ description: t("audienceOverview.predictedGroupsTooltip") }}
            />
          </div>
          <p className="audience-chart__subtitle">
            {t("audienceOverview.predictedGroupsSubtitle")}{" "}
            <strong>{t("audienceOverview.predictedGroupsSubtitleEmphasis")}</strong>.
          </p>
        </div>
        <div className="audience-chart__legend">
          <span className="audience-chart__legend-item">
            <span className="audience-chart__legend-dot audience-chart__legend-dot--negative" />
            {t("audienceOverview.needsAttention")}
          </span>
          <span className="audience-chart__legend-item">
            <span className="audience-chart__legend-dot audience-chart__legend-dot--positive" />
            {t("audienceOverview.movingRightWay")}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="audience-chart__loading">
          <Spin />
        </div>
      ) : data.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={pageData} margin={{ top: 24, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid vertical={false} stroke={CHART_GRID_STROKE} />
              <CartesianGrid horizontal={false} strokeDasharray="4 4" stroke={CHART_GRID_STROKE} />
              <XAxis
                dataKey="name"
                interval={0}
                height={X_AXIS_HEIGHT}
                axisLine={false}
                tickLine={false}
                tick={<GroupAxisTick groups={pageData} formatTrend={formatTrend} />}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={48}
                allowDecimals={false}
                domain={[0, "auto"]}
                tickFormatter={formatValue}
                tick={{ className: "audience-chart__axis-tick" }}
              />
              <Tooltip
                cursor={{ fill: CHART_TOOLTIP_CURSOR }}
                content={
                  <CustomChartTooltip
                    title={t("audienceOverview.members")}
                    formatTooltip={formatValue}
                  />
                }
              />
              <Bar dataKey="members" fill={BAR_COLOR} radius={[6, 6, 0, 0]} barSize={BAR_SIZE}>
                <LabelList
                  dataKey="membersLabel"
                  position="top"
                  className="audience-chart__value-label"
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="audience-chart__pagination">
            <button
              type="button"
              className="audience-chart__page-btn"
              aria-label={t("pagination.previousPage")}
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
            >
              <PreviousIcon size={16} aria-hidden />
            </button>
            <button
              type="button"
              className="audience-chart__page-btn"
              aria-label={t("pagination.nextPage")}
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
            >
              <NextIcon size={16} aria-hidden />
            </button>
          </div>
        </>
      ) : (
        <NoDataDisplay
          message={t("audienceOverview.noChartData")}
          altText={t("audienceOverview.noDataAlt")}
        />
      )}
    </section>
  );
};

export default memo(AudienceBarChart);
